import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { ShowService } from '../services/show.service';
import * as ShowActions from './show.actions';

@Injectable()
export class ShowsEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private logger = inject(LoggerService);
    private _showService = inject(ShowService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all shows',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get all shows
    getAllShows$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ShowActions.loadShows),
            switchMap(() => {
                return this._showService.getAll().pipe(
                    // tap((res) => this.logger.log('All shows', res)),
                    map((res) => {
                        return ShowActions.loadShowsSuccess({
                            shows: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ShowActions.loadShowsFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One Show
    getShow$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ShowActions.loadShow),
            switchMap((action) => {
                return this._showService.getOne(action.id).pipe(
                    map((res) => {
                        return ShowActions.loadShowSuccess({
                            show: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ShowActions.loadShowFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create Show
    createShow$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ShowActions.createShow),
            switchMap((action) => {
                return this._showService.create(action.showData).pipe(
                    map((res) => {
                        return ShowActions.createShowSuccess({
                            show: res.data,
                            success: 'Show created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ShowActions.createShowFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Show
    updateShow$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ShowActions.updateShow),
            switchMap((action) => {
                return this._showService
                    .update(action.showData, action.id)
                    .pipe(
                        map((res) => {
                            return ShowActions.updateShowSuccess({
                                show: res.data,
                                success: 'Show updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                ShowActions.updateShowFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete Show
    deleteShow$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ShowActions.deleteShow),
            switchMap((action) => {
                return this._showService.delete(action.id).pipe(
                    map(() => {
                        return ShowActions.deleteShowSuccess({
                            id: action.id,
                            success: 'Show deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ShowActions.deleteShowFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onShowActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ShowActions.createShowSuccess,
                    ShowActions.updateShowSuccess,
                    ShowActions.deleteShowSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onShowActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ShowActions.loadShowsFailure,
                    ShowActions.loadShowFailure,
                    ShowActions.createShowFailure,
                    ShowActions.updateShowFailure,
                    ShowActions.deleteShowFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
